import { Theme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';

import {
  CALL_FRAME_ATTRIBUTE,
  CALL_MIN_VARIABLE,
  DOCK_BAR_VARIABLE,
} from 'components/Alpha/LiveAssist/liveAssistFit';

export const LA_FONT = {
  caption: { fontSize: '12px', lineHeight: '16px' },
  small: { fontSize: '13px', lineHeight: '18px' },
  body: { fontSize: '15px', lineHeight: '22px' },
} as const;

export const LA_WEIGHT = { regular: 500, strong: 700 } as const;

const muted = (theme: Theme): string => alpha(theme.palette.text.primary, 0.68);
const hairline = (theme: Theme): string => alpha(theme.palette.text.primary, 0.14);
const fill = (theme: Theme): string => alpha(theme.palette.text.primary, 0.05);

const focusRing = (theme: Theme): Record<string, unknown> => ({
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.text.primary}`,
    outlineOffset: 2,
  },
});

export const useLiveAssistFrameStyles = makeStyles({
  frame: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    width: '100%',
    minWidth: 0,
    overflow: 'hidden',
    '& > :first-child': {
      flex: '1 1 0',
      minHeight: `min(var(${CALL_MIN_VARIABLE}, 0px), calc(100% - var(${DOCK_BAR_VARIABLE}, 0px)))`,
      height: 'auto',
    },
  },
});

export const useLiveAssistStyles = makeStyles((theme: Theme) => ({
  sheet: {
    flex: '0 1 auto',
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    minHeight: 0,
    overflow: 'hidden',
    isolation: 'isolate',
    background: theme.palette.background.paper,
    color: theme.palette.text.primary,
    borderTop: `1px solid ${hairline(theme)}`,
    ...LA_FONT.small,
  },
  sheetOpen: { [`[${CALL_FRAME_ATTRIBUTE}] &`]: { flex: '1 1 0' } },
  visuallyHidden: {
    position: 'absolute',
    width: 1,
    height: 1,
    margin: -1,
    padding: 0,
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
    border: 0,
  },
  header: {
    flex: '0 0 auto',
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    columnGap: theme.spacing(1),
    rowGap: theme.spacing(0.5),
    minHeight: 44,
    padding: theme.spacing(0, 1, 0, 0.5),
    minWidth: 0,
  },
  toggle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flex: '1 1 auto',
    minWidth: 0,
    minHeight: 40,
    padding: theme.spacing(0, 1),
    border: 0,
    borderRadius: 8,
    background: 'transparent',
    color: 'inherit',
    cursor: 'pointer',
    textAlign: 'left',
    fontFamily: 'inherit',
    '&:hover': { background: fill(theme) },
    ...focusRing(theme),
  },
  title: {
    ...LA_FONT.body,
    fontWeight: LA_WEIGHT.strong,
  },
  badge: {
    ...LA_FONT.caption,
    fontWeight: LA_WEIGHT.strong,
    padding: '2px 8px',
    borderRadius: 999,
    background: theme.palette.callScribe.accent,
    color: theme.palette.primary.main,
    whiteSpace: 'nowrap',
  },
  chevron: {
    marginLeft: 'auto',
    display: 'flex',
    color: muted(theme),
  },
  chip: {
    ...LA_FONT.caption,
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 1.5),
    borderRadius: 999,
    background: fill(theme),
    border: `1px solid ${hairline(theme)}`,
    fontWeight: LA_WEIGHT.strong,
    whiteSpace: 'nowrap',
    flex: '0 0 auto',
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    flex: '0 0 auto',
  },
  dotListening: { background: theme.palette.success.main },
  dotPaused: { background: theme.palette.warning.main },
  dotIdle: { background: muted(theme) },
  headerButton: {
    ...LA_FONT.small,
    flex: '0 0 auto',
    minHeight: 32,
    padding: theme.spacing(0.5, 1),
    textTransform: 'none',
    fontWeight: LA_WEIGHT.strong,
    color: theme.palette.text.primary,
    ...focusRing(theme),
  },
  body: {
    flex: '0 1 auto',
    display: 'flex',
    flexDirection: 'column',
    height: 'clamp(220px, 28vh, 420px)',
    minHeight: 0,
    borderTop: `1px solid ${hairline(theme)}`,
    [`[${CALL_FRAME_ATTRIBUTE}] &`]: { flex: '1 1 auto', height: 'auto' },
  },
  tabs: {
    minHeight: 36,
    flex: '0 0 auto',
    padding: theme.spacing(0, 1),
    '& .MuiTabs-indicator': { background: theme.palette.text.primary },
  },
  tab: {
    ...LA_FONT.small,
    minHeight: 36,
    minWidth: 0,
    padding: theme.spacing(0.5, 1.5),
    textTransform: 'none',
    fontWeight: LA_WEIGHT.strong,
    color: muted(theme),
    '&.Mui-selected': { color: theme.palette.text.primary },
    ...focusRing(theme),
  },
  tabPanel: {
    flex: '1 1 auto',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  panel: {
    flex: '1 1 auto',
    minHeight: 0,
    overflowY: 'auto',
    padding: theme.spacing(1.5),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    outline: 'none',
  },
  hiddenPanel: { display: 'none' },
  notice: {
    ...LA_FONT.small,
    flex: '0 0 auto',
    '& .MuiAlert-message': { ...LA_FONT.small },
  },
  noticeButton: {
    ...LA_FONT.small,
    textTransform: 'none',
    fontWeight: LA_WEIGHT.strong,
    color: 'inherit',
    ...focusRing(theme),
  },
  replayRow: {
    display: 'flex',
    justifyContent: 'flex-start',
  },
  secondaryButton: {
    ...LA_FONT.small,
    textTransform: 'none',
    fontWeight: LA_WEIGHT.strong,
    color: theme.palette.text.primary,
    borderColor: hairline(theme),
    ...focusRing(theme),
  },
  empty: {
    ...LA_FONT.small,
    color: muted(theme),
    margin: 0,
    padding: theme.spacing(1, 0),
  },
  skeletonCard: {
    height: 72,
    borderRadius: 8,
    background: fill(theme),
  },
  feedList: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
    borderRadius: 8,
    border: `1px solid ${hairline(theme)}`,
    background: theme.palette.background.paper,
    minWidth: 0,
    outline: 'none',
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.text.primary}`,
      outlineOffset: 2,
    },
  },
  cardPinned: {
    borderColor: alpha(theme.palette.text.primary, 0.4),
  },
  cardTop: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    minWidth: 0,
  },
  kind: {
    ...LA_FONT.caption,
    fontWeight: LA_WEIGHT.strong,
    color: muted(theme),
    flex: '1 1 auto',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    flex: '0 0 auto',
  },
  iconButton: {
    width: 32,
    height: 32,
    padding: 6,
    color: muted(theme),
    '&:hover': { background: fill(theme), color: theme.palette.text.primary },
    '&[aria-pressed="true"]': { color: theme.palette.text.primary },
    ...focusRing(theme),
  },
  cardTitle: {
    ...LA_FONT.body,
    fontWeight: LA_WEIGHT.strong,
    margin: 0,
    overflowWrap: 'anywhere',
  },
  cardBody: {
    ...LA_FONT.small,
    overflowWrap: 'anywhere',
    '& p, & li, & span, & strong, & em, & a, & td, & th': { ...LA_FONT.small },
    '& p': { whiteSpace: 'pre-line' },
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(0.75),
    minWidth: 0,
  },
  detailLine: {
    ...LA_FONT.small,
    margin: 0,
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.75),
  },
  detailLabel: {
    ...LA_FONT.small,
    fontWeight: LA_WEIGHT.strong,
  },
  detailText: {
    ...LA_FONT.small,
    overflowWrap: 'anywhere',
  },
  detailMeta: {
    ...LA_FONT.caption,
    color: muted(theme),
  },
  detailNotice: {
    ...LA_FONT.small,
    margin: 0,
    color: muted(theme),
  },
  section: {
    alignSelf: 'stretch',
    borderTop: `1px solid ${hairline(theme)}`,
    paddingTop: theme.spacing(0.75),
  },
  sectionSummary: {
    ...LA_FONT.small,
    fontWeight: LA_WEIGHT.strong,
    cursor: 'pointer',
    borderRadius: 4,
    padding: theme.spacing(0.25, 0),
    ...focusRing(theme),
  },
  detailList: {
    listStyle: 'none',
    margin: theme.spacing(0.75, 0, 0),
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
  },
  detailItem: {
    ...LA_FONT.small,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    minWidth: 0,
  },
  sources: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  sourcesLabel: {
    ...LA_FONT.caption,
    fontWeight: LA_WEIGHT.strong,
    color: muted(theme),
  },
  sourcesList: {
    margin: 0,
    paddingLeft: 20,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  source: {
    ...LA_FONT.small,
    overflowWrap: 'anywhere',
  },
  sourceLink: {
    ...LA_FONT.small,
    color: theme.palette.text.primary,
    textDecoration: 'underline',
    borderRadius: 2,
    ...focusRing(theme),
  },
  sourceOrigin: {
    ...LA_FONT.small,
    color: muted(theme),
  },
  actions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  actionButton: {
    ...LA_FONT.small,
    minHeight: 32,
    textTransform: 'none',
    fontWeight: LA_WEIGHT.strong,
    color: theme.palette.text.primary,
    borderColor: hairline(theme),
    ...focusRing(theme),
  },
  reviewButton: {
    ...LA_FONT.small,
    minHeight: 32,
    textTransform: 'none',
    fontWeight: LA_WEIGHT.strong,
    ...focusRing(theme),
  },
  review: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    minWidth: 0,
  },
  outcome: {
    ...LA_FONT.small,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    padding: theme.spacing(1),
    borderRadius: 6,
    background: fill(theme),
    '& p, & li, & span, & strong': { ...LA_FONT.small },
  },
  outcomeLabel: {
    ...LA_FONT.caption,
    fontWeight: LA_WEIGHT.strong,
    color: muted(theme),
  },
  askList: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  askItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
  },
  question: {
    ...LA_FONT.small,
    alignSelf: 'flex-end',
    maxWidth: '85%',
    padding: theme.spacing(0.75, 1.25),
    borderRadius: 10,
    background: fill(theme),
    border: `1px solid ${hairline(theme)}`,
    overflowWrap: 'anywhere',
    whiteSpace: 'pre-wrap',
  },
  answer: {
    ...LA_FONT.small,
    overflowWrap: 'anywhere',
    '& p, & li, & span, & strong, & em, & a, & td, & th': { ...LA_FONT.small },
  },
  thinking: {
    ...LA_FONT.small,
    color: muted(theme),
    margin: 0,
  },
  askError: {
    ...LA_FONT.small,
    '& .MuiAlert-message': { ...LA_FONT.small },
  },
  composer: {
    flex: '0 0 auto',
    display: 'flex',
    alignItems: 'flex-end',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1.5, 1.5),
    borderTop: `1px solid ${hairline(theme)}`,
  },
  composerField: {
    flex: '1 1 auto',
    minWidth: 0,
    '& .MuiInputBase-input': { ...LA_FONT.small },
    '& .MuiInputLabel-root': { ...LA_FONT.small },
    '& .MuiFormHelperText-root': { ...LA_FONT.caption },
  },
  sendButton: {
    ...LA_FONT.small,
    flex: '0 0 auto',
    minHeight: 40,
    textTransform: 'none',
    fontWeight: LA_WEIGHT.strong,
    ...focusRing(theme),
  },
}));
